import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Steps,
  Card,
  Form,
  Input,
  Button,
  Space,
  Typography,
  Alert,
  message,
  Radio,
  InputNumber,
  Divider,
  Result,
  Spin,
  AutoComplete,
  Tag
} from 'antd';
import {
  ArrowLeftOutlined,
  SearchOutlined,
  PlusOutlined,
  SaveOutlined,
  ShopOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useStores } from '../../../stores';
import partnerService from '../../../services/partnerService';
import partnerInventoryService from '../../../services/partnerInventoryService';
import './Inventory.scss';

const { Step } = Steps;
const { Title, Text } = Typography;

const InventoryAdd = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { partnerInventoryStore } = useStores();
  const { matchedProduct, productVariants, selectedVariant, loading, error } = partnerInventoryStore;

  const [currentStep, setCurrentStep] = useState(0);
  const [productSearchForm] = Form.useForm();
  const [variantForm] = Form.useForm();
  const [inventoryForm] = Form.useForm();
  const [itemsInfo, setItemsInfo] = useState(null);
  const [checkingLimit, setCheckingLimit] = useState(true);
  const [searchOptions, setSearchOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [variantChoice, setVariantChoice] = useState(null); // 'existing' or 'new'

  useEffect(() => {
    // Reset workflow when component mounts
    partnerInventoryStore.resetWorkflow();
    checkItemLimit();
  }, [partnerInventoryStore]);

  const checkItemLimit = async () => {
    setCheckingLimit(true);
    const result = await partnerService.fetchItemsInfo();
    if (result.success) {
      setItemsInfo(result.data);
    }
    setCheckingLimit(false);
  };

  const isLimitReached = itemsInfo && itemsInfo.items_remaining <= 0;

  // Debounced search for products
  const searchTimeoutRef = React.useRef(null);

  const handleSearch = async (value) => {
    setSearchValue(value);
    setSelectedProduct(null);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.length < 2) {
      setSearchOptions([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setSearching(true);
      const result = await partnerInventoryService.searchProducts(value);
      setSearching(false);

      if (result.success && result.data) {
        const options = result.data.map(product => ({
          value: product.name,
          label: (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{product.title_en || product.name}</span>
              {product.is_marketing_complete && (
                <Tag color="green" style={{ marginLeft: 8 }}>
                  <CheckCircleOutlined /> {t('partner.products.available')}
                </Tag>
              )}
            </div>
          ),
          product: product
        }));
        setSearchOptions(options);
      }
    }, 300);
  };

  const handleSelect = (value, option) => {
    setSelectedProduct(option.product);
    setSearchValue(value);
  };

  // Show limit reached screen
  if (checkingLimit) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>{t('partner.products.checkingAvailability')}</div>
      </div>
    );
  }

  if (isLimitReached) {
    return (
      <div className="inventory-add">
        <div className="page-header">
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/partner/inventory')}
            >
              {t('partner.products.backToProducts')}
            </Button>
          </Space>
        </div>

        <Result
          status="warning"
          icon={<ShopOutlined style={{ color: '#faad14' }} />}
          title={t('partner.products.limitReached')}
          subTitle={t('partner.products.limitReachedDescItems', { max: itemsInfo.max_items })}
          extra={[
            <Button type="primary" key="packages" onClick={() => navigate('/partner/packages')}>
              {t('partner.products.getMoreSlots')}
            </Button>,
            <Button key="back" onClick={() => navigate('/partner/inventory')}>
              {t('partner.products.backToProducts')}
            </Button>
          ]}
        />
      </div>
    );
  }

  // Step 1: Search and match product
  const handleProductSearch = async (values) => {
    const result = await partnerInventoryStore.matchProduct(values.productName);

    if (result.success) {
      const isMatched = result.data.matched;
      const product = result.data.product;

      if (isMatched) {
        message.success(t('partner.products.messages.matchSuccess'));
        // Fetch variants for matched product
        await partnerInventoryStore.fetchProductVariants(product.id);
      } else {
        message.info(t('partner.products.messages.newProductCreated'));
      }

      setCurrentStep(1);
    } else {
      message.error(result.error || t('partner.products.messages.matchFailed'));
    }
  };

  // Step 2: Select or create variant
  const handleVariantSelection = () => {
    if (variantChoice === 'existing' && !selectedVariant) {
      message.error(t('partner.products.messages.selectVariant'));
      return;
    }
    setCurrentStep(2);
  };

  const handleVariantCreation = async (values) => {
    const result = await partnerInventoryStore.createVariant(matchedProduct.id, values);

    if (result.success) {
      message.success(t('partner.products.messages.variantCreated'));
      setCurrentStep(2);
    } else {
      message.error(result.error || t('partner.products.messages.variantFailed'));
    }
  };

  // Step 3: Set inventory details
  const handleInventorySubmit = async (values) => {
    if (!selectedVariant) {
      message.error(t('partner.products.messages.noVariant'));
      return;
    }

    const inventoryData = {
      product_variant: selectedVariant.id,
      price: values.price,
      shipping_cost: values.shipping_cost,
      stock_quantity: values.stock_quantity,
      is_active: values.is_active !== false,
    };

    const result = await partnerInventoryStore.createInventory(inventoryData);

    if (result.success) {
      message.success(t('partner.products.messages.createSuccess'));
      // Refresh inventory list before navigating
      await partnerInventoryStore.fetchInventory();
      navigate('/partner/inventory');
    } else {
      message.error(result.error || t('partner.products.messages.createFailed'));
    }
  };

  const steps = [
    {
      title: t('partner.products.steps.matchProduct'),
      content: (
        <Card>
          <Form
            form={productSearchForm}
            layout="vertical"
            onFinish={handleProductSearch}
          >
            <Form.Item
              name="productName"
              label={t('partner.products.form.productName')}
              rules={[{ required: true, message: t('partner.products.form.productNameRequired') }]}
            >
              <AutoComplete
                options={searchOptions}
                onSearch={handleSearch}
                onSelect={handleSelect}
                value={searchValue}
                onChange={setSearchValue}
                placeholder={t('partner.products.form.productNamePlaceholder')}
                size="large"
                style={{ width: '100%' }}
                notFoundContent={searching ? <Spin size="small" /> : null}
              >
                <Input
                  size="large"
                  prefix={<SearchOutlined />}
                  suffix={searching ? <Spin size="small" /> : null}
                />
              </AutoComplete>
            </Form.Item>

            {selectedProduct && (
              <Alert
                message={t('partner.products.steps.productFound')}
                description={
                  <span>
                    {selectedProduct.title_en || selectedProduct.name}
                    {selectedProduct.is_marketing_complete && (
                      <Tag color="green" style={{ marginLeft: 8 }}>
                        <CheckCircleOutlined /> {t('partner.products.ready')}
                      </Tag>
                    )}
                  </span>
                }
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {!selectedProduct && (
              <Alert
                message={t('partner.products.steps.productMatching')}
                description={t('partner.products.steps.productMatchingDesc')}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <Button type="primary" htmlType="submit" loading={loading} size="large">
              {selectedProduct ? t('partner.products.steps.useThisProduct') : t('partner.products.steps.searchProduct')}
            </Button>
          </Form>
        </Card>
      ),
    },
    {
      title: t('partner.products.steps.selectVariant'),
      content: (
        <Card>
          {matchedProduct && (
            <>
              <Title level={4}>{t('partner.products.steps.product')}: {matchedProduct.name}</Title>
              <Divider />

              {productVariants.length > 0 ? (
                <>
                  <Text>{t('partner.products.steps.chooseOption')}:</Text>
                  <Radio.Group
                    onChange={(e) => setVariantChoice(e.target.value)}
                    value={variantChoice}
                    style={{ display: 'block', marginTop: 16, marginBottom: 24 }}
                  >
                    <Radio value="existing">{t('partner.products.steps.selectExisting')}</Radio>
                    <Radio value="new">{t('partner.products.steps.createNew')}</Radio>
                  </Radio.Group>

                  {variantChoice === 'existing' && (
                    <div>
                      <Text strong>{t('partner.products.steps.selectAVariant')}:</Text>
                      <Radio.Group
                        onChange={(e) => {
                          const variant = productVariants.find(v => v.id === e.target.value);
                          partnerInventoryStore.setSelectedVariant(variant);
                        }}
                        value={selectedVariant?.id}
                        style={{ display: 'block', marginTop: 12 }}
                      >
                        {productVariants.map(variant => (
                          <Radio key={variant.id} value={variant.id} style={{ display: 'block', marginBottom: 8 }}>
                            {t('partner.products.sku')}: {variant.sku}
                            {variant.color && ` | ${t('partner.products.color')}: ${variant.color}`}
                            {variant.size && ` | ${t('partner.products.size')}: ${variant.size}`}
                            {variant.weight && ` | ${t('partner.products.weight')}: ${variant.weight}g`}
                          </Radio>
                        ))}
                      </Radio.Group>
                      <Button
                        type="primary"
                        onClick={handleVariantSelection}
                        style={{ marginTop: 16 }}
                        disabled={!selectedVariant}
                      >
                        {t('partner.products.steps.continueWithVariant')}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <Alert
                  message={t('partner.products.steps.noVariants')}
                  description={t('partner.products.steps.noVariantsDesc')}
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              {(variantChoice === 'new' || productVariants.length === 0) && (
                <Form
                  form={variantForm}
                  layout="vertical"
                  onFinish={handleVariantCreation}
                  style={{ marginTop: 16 }}
                >
                  <Form.Item
                    name="sku"
                    label={t('partner.products.form.sku')}
                    rules={[{ required: true, message: t('partner.products.form.skuRequired') }]}
                  >
                    <Input placeholder={t('partner.products.form.skuPlaceholder')} />
                  </Form.Item>

                  <Form.Item name="color" label={t('partner.products.form.colorOptional')}>
                    <Input placeholder={t('partner.products.form.colorPlaceholder')} />
                  </Form.Item>

                  <Form.Item name="size" label={t('partner.products.form.sizeOptional')}>
                    <Input placeholder={t('partner.products.form.sizePlaceholder')} />
                  </Form.Item>

                  <Form.Item name="weight" label={t('partner.products.form.weightOptional')}>
                    <InputNumber
                      placeholder={t('partner.products.form.weightPlaceholder')}
                      style={{ width: '100%' }}
                      min={0}
                    />
                  </Form.Item>

                  <Button type="primary" htmlType="submit" loading={loading}>
                    {t('partner.products.steps.createVariantContinue')}
                  </Button>
                </Form>
              )}
            </>
          )}
        </Card>
      ),
    },
    {
      title: t('partner.products.steps.setPricing'),
      content: (
        <Card>
          {selectedVariant && (
            <>
              <Title level={4}>
                {matchedProduct.name}
              </Title>
              <Text type="secondary">
                {t('partner.products.sku')}: {selectedVariant.sku}
                {selectedVariant.color && ` | ${t('partner.products.color')}: ${selectedVariant.color}`}
                {selectedVariant.size && ` | ${t('partner.products.size')}: ${selectedVariant.size}`}
              </Text>
              <Divider />

              <Form
                form={inventoryForm}
                layout="vertical"
                onFinish={handleInventorySubmit}
                initialValues={{
                  is_active: true,
                }}
              >
                <Form.Item
                  name="price"
                  label={t('partner.products.form.price')}
                  rules={[
                    { required: true, message: t('partner.products.form.priceRequired') },
                    { type: 'number', min: 0.01, message: t('partner.products.form.priceMin') },
                  ]}
                >
                  <InputNumber
                    placeholder={t('partner.products.form.pricePlaceholder')}
                    style={{ width: '100%' }}
                    precision={2}
                    min={0}
                    step={0.01}
                  />
                </Form.Item>

                <Form.Item
                  name="shipping_cost"
                  label={t('partner.products.form.shippingCost')}
                  rules={[
                    { required: true, message: t('partner.products.form.shippingRequired') },
                    { type: 'number', min: 0, message: t('partner.products.form.shippingMin') },
                  ]}
                >
                  <InputNumber
                    placeholder={t('partner.products.form.shippingPlaceholder')}
                    style={{ width: '100%' }}
                    precision={2}
                    min={0}
                    step={0.01}
                  />
                </Form.Item>

                <Form.Item
                  name="stock_quantity"
                  label={t('partner.products.form.stockQuantity')}
                  rules={[
                    { required: true, message: t('partner.products.form.stockRequired') },
                    { type: 'number', min: 0, message: t('partner.products.form.stockMin') },
                  ]}
                >
                  <InputNumber
                    placeholder={t('partner.products.form.stockPlaceholder')}
                    style={{ width: '100%' }}
                    min={0}
                  />
                </Form.Item>

                <Form.Item name="is_active" label={t('common.active')} valuePropName="checked">
                  <Radio.Group>
                    <Radio value={true}>{t('common.yes')}</Radio>
                    <Radio value={false}>{t('common.no')}</Radio>
                  </Radio.Group>
                </Form.Item>

                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                  size="large"
                >
                  {t('partner.products.saveProduct')}
                </Button>
              </Form>
            </>
          )}
        </Card>
      ),
    },
  ];

  return (
    <div className="inventory-add">
      <div className="page-header">
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/partner/inventory')}
          >
            {t('partner.products.backToInventory')}
          </Button>
          <Title level={2}>{t('partner.products.addProduct')}</Title>
        </Space>
      </div>

      {error && (
        <Alert
          message={t('common.error')}
          description={error}
          type="error"
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      <Card style={{ marginBottom: 16 }}>
        <Steps current={currentStep}>
          {steps.map((step, index) => (
            <Step key={index} title={step.title} />
          ))}
        </Steps>
      </Card>

      <div className="steps-content">{steps[currentStep].content}</div>
    </div>
  );
});

export default InventoryAdd;
